import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch } from "react-redux";
import Card from "@/components/ui/Card";
import Icon from "@/components/ui/Icon";
import Tooltip from "@/components/ui/Tooltip";
import {
  useTable,
  useRowSelect,
  useSortBy,
  useGlobalFilter,
  usePagination,
} from "react-table";
import GlobalFilter from "../table/react-tables/GlobalFilter";
import { FaCheckCircle } from "react-icons/fa";
import { MdCancel } from "react-icons/md";
import Select from "react-select";
import FilterButton from "@/components/ui/FilterButton";
import { fetchProfiles } from '../../server/profiles/profilesServer';
import { setProfiles, setLoading, setError } from '../../store/api/profiles/profileSlice';
import { useNavigate } from 'react-router-dom';

const UnverifiedProfiles = ({ title = "Unverified Profiles" }) => {
  const [profiles, setProfilesState] = useState([]);
  const [loading, setLoadingState] = useState(true);
  const [pageCount, setPageCount] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState("");

  const fetchData = async (pageIndex, pageSize, search = undefined) => {
    setLoadingState(true);
    dispatch(setLoading(true));
    try {
      const { data, pagination } = await fetchProfiles(
        true, // Fetch only unverified profiles
        pageIndex + 1,
        pageSize,
        undefined, // No active filter
        false, // Only unverified profiles
        search
      );
      setProfilesState(data || []);
      dispatch(setProfiles(data || []));
      setPageCount(pagination.pages);
    } catch (err) {
      console.error(err);
      dispatch(setError(err.toString()));
    } finally {
      setLoadingState(false);
      dispatch(setLoading(false));
    }
  };

  useEffect(() => {
    fetchData(pageIndex, pageSize);
  }, [pageIndex, pageSize]);

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    fetchData(pageIndex, pageSize, value);
  };

  const COLUMNS = [
    {
      Header: "Username",
      accessor: "username",
      Cell: ({ row }) => (
        <div>
          <span className="inline-flex items-center">
            <span className="w-7 h-7 rounded-full ltr:mr-3 rtl:ml-3 flex-none bg-slate-600">
              <img
                src={row.original.picture}
                alt=""
                className="object-cover w-full h-full rounded-full"
              />
            </span>
            <span className="text-sm text-slate-600 dark:text-slate-300 capitalize">
              {row.original.username}
            </span>
          </span>
        </div>
      ),
    },
    {
      Header: "Type",
      accessor: "profileType",
    },
    {
      Header: "SubType",
      accessor: "profileSubType",
      Cell: ({ value }) => <span>{value || "-"}</span>,
    },
    {
      Header: "Status",
      accessor: "isActive",
      Cell: ({ value }) => (
        <span>{value ? <FaCheckCircle color="green" size={22} /> : <MdCancel color="red" size={26} />}</span>
      ),
    },
    {
      Header: "Verified",
      accessor: "isVerified",
      Cell: () => <MdCancel color="red" size={26} />, // Always unverified
    },
    {
      Header: "Created at",
      accessor: "createdAt",
      Cell: ({ value }) => {
        const date = new Date(value);
        return <span>{date.toLocaleDateString()}</span>;
      },
    },
    {
      Header: "Updated at",
      accessor: "updatedAt",
      Cell: ({ value }) => {
        const date = new Date(value);
        return <span>{date.toLocaleDateString()}</span>;
      },
    },
    {
      Header: "Actions",
      accessor: "actions",
      Cell: ({ row }) => (
        <div className="flex space-x-3 rtl:space-x-reverse justify-center">
          <Tooltip content="Edit" placement="top" arrow animation="shift-away">
            <button
              className="action-btn bg-black-500 text-white rounded-md"
              type="button"
              onClick={() => navigate(`/detail-profile/${row.original._id}`)}
            >
              <Icon icon="heroicons:pencil-square" />
            </button>
          </Tooltip>
        </div>
      ),
    },
  ];

  const columns = useMemo(() => COLUMNS, []);
  const data = useMemo(() => profiles, [profiles]);

  const tableInstance = useTable(
    {
      columns,
      data,
      manualPagination: true,
      pageCount,
      initialState: { pageIndex },
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    state,
    setGlobalFilter,
    page,
  } = tableInstance;

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <h4 className="card-title font-extrabold">{title}</h4>
      </div>
      <Card>
        <div className="md:flex justify-between items-center mb-6 space-y-4 md:space-y-0">
          <GlobalFilter filter={searchTerm} setFilter={handleSearchChange} />
        </div>
        <div className="overflow-x-auto -mx-6">
          <div className="inline-block min-w-full align-middle">
            <div className="overflow-hidden">
              {loading ? (
                <p>Loading...</p>
              ) : (
                <table
                  className="min-w-full divide-y divide-slate-100 table-fixed dark:divide-slate-700"
                  {...getTableProps()}
                >
                  <thead className="bg-white dark:bg-slate-700">
                    {headerGroups.map((headerGroup) => (
                      <tr {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map((column) => (
                          <th {...column.getHeaderProps(column.getSortByToggleProps())} className="table-th">
                            {column.render("Header")}
                            <span>{column.isSorted ? (column.isSortedDesc ? " 🔽" : " 🔼") : ""}</span>
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody {...getTableBodyProps()} className="bg-white divide-y dark:bg-slate-800">
                    {page.map((row) => {
                      prepareRow(row);
                      return (
                        <tr {...row.getRowProps()}>
                          {row.cells.map((cell) => (
                            <td {...cell.getCellProps()} className="table-td">
                              {cell.render("Cell")}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </Card>
    </>
  );
};

export default UnverifiedProfiles;
